import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, Link } from "react-router-dom";
import { Toaster, toast } from "sonner";
import axios from "axios";

// Components
import Landing from "./pages/Landing";
import LoginPage from "./pages/LoginPage";
import RegisterSalon from "./pages/RegisterSalon";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import CookiePolicy from "./pages/CookiePolicy";
import TermsConditions from "./pages/TermsConditions";
import DataProcessingAgreement from "./pages/DataProcessingAgreement";
import TrustSecurity from "./pages/TrustSecurity";
import BookingPage from "./pages/BookingPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import SubscriptionRequiredPage from "./pages/SubscriptionRequiredPage";
import AuthCallback from "./pages/AuthCallback";
import ConfirmAppointment from "./pages/ConfirmAppointment";
import VerticalLanding from "./pages/VerticalLanding";
import DentistaLanding from "./pages/DentistaLanding";
import { dentistiSector, esteticaSector, fisioterapiaSector, hairSector } from "./data/sectorData";
import Dashboard from "./pages/Dashboard";
import SalesDashboard from "./pages/SalesDashboardV2";
import WhatsAppDashboard from "./pages/WhatsAppDashboard";
import InteractiveDemoPage from "./pages/InteractiveDemoPage";
import DemoLive from "./pages/DemoLive";
import DemoOdonto from "./pages/DemoOdonto";
import DemoBenesserePosturale from "./pages/DemoBenesserePosturale";
import DemoEstetica from "./pages/DemoEstetica";
import DemoHair from "./pages/DemoHair";
import OnboardingPage from "./pages/OnboardingPage";
import MagicLinkVerify from "./pages/MagicLinkVerify";
import Services from "./pages/Services";
import Staff from "./pages/Staff";
import Clients from "./pages/Clients";
import ClientProfile from "./pages/ClientProfile";
import VideoDemoPage from "./pages/VideoDemoPage";
import VideoDemoVoiceOverPage from "./pages/VideoDemoVoiceOverPage";
import DentalMigrationPage from "./pages/dental/MigrationPage";
import DentalIntakePage from "./pages/dental/PatientIntakePage";
import ConsentTemplatesPage from "./pages/dental/ConsentTemplatesPage";
import PrescriptionsHubPage from "./pages/dental/PrescriptionsHubPage";
import DentalProTrialChecklist from "./pages/dental/DentalProTrialChecklist";
import PublicSignPage from "./pages/PublicSignPage";
import PublicExerciseRoutine from "./pages/PublicExerciseRoutine";
import PatientPortal from "./pages/PatientPortal";
import PatientPortalLogin from "./pages/PatientPortalLogin";
import Appointments from "./pages/Appointments";
import CalendarView from "./pages/CalendarView";
import Reviews from "./pages/Reviews";
import Settings from "./pages/Settings";
import SmartPageEditor from "./pages/SmartPageEditor";
import BenesserePosturaleLanding from "./pages/BenesserePosturaleLanding";
import SuperAdmin from "./pages/SuperAdmin";
import ClientPortal from "./pages/ClientPortal";
import Analytics from "./pages/Analytics";
import PWAInstallBanner from "./components/PWAInstallBanner";
import CookieBanner from "./components/CookieBanner";
import { TierProvider } from "./components/FeatureGate";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Translations
const translations = {
  it: {
    home: "Home",
    dashboard: "Dashboard",
    services: "Servizi",
    staff: "Team",
    clients: "Clienti",
    appointments: "Appuntamenti",
    calendar: "Calendario",
    reviews: "Recensioni",
    settings: "Impostazioni",
    logout: "Esci",
    login: "Accedi",
    book_now: "Prenota Ora",
    loading: "Caricamento...",
    error: "Errore",
    success: "Successo",
    cancel: "Annulla",
    save: "Salva",
    delete: "Elimina",
    edit: "Modifica",
    add: "Aggiungi",
    search: "Cerca...",
    no_results: "Nessun risultato",
    welcome_back: "Bentornato",
    today_appointments: "Appuntamenti Oggi",
    total_clients: "Clienti Totali",
    monthly_revenue: "Fatturato Mensile",
    no_show_rate: "Tasso No-Show",
    popular_services: "Servizi Popolari",
    new_clients: "Nuovi Clienti",
    select_service: "Seleziona Servizio",
    select_date: "Seleziona Data",
    select_time: "Seleziona Orario",
    your_info: "I Tuoi Dati",
    confirm_booking: "Conferma Prenotazione",
    booking_confirmed: "Prenotazione Confermata!",
    name: "Nome",
    email: "Email",
    phone: "Telefono",
    notes: "Note",
    duration: "Durata",
    price: "Prezzo",
    category: "Categoria",
    status: "Stato",
    date: "Data",
    time: "Orario",
    actions: "Azioni",
    pending: "In Attesa",
    awaiting_confirmation: "Attende Conferma",
    confirmed: "Confermato",
    completed: "Completato",
    cancelled: "Annullato",
    no_show: "No Show",
    unconfirmed: "Non Confermato",
    minutes: "minuti",
    any_staff: "Primo Disponibile",
    closed: "Chiuso",
    admin_panel: "Pannello Admin",
    super_admin: "Super Admin",
    tenants: "Clienti",
    subscriptions: "Abbonamenti",
    create_salon: "Nuova Attività",
    manage_salons: "Gestione Clienti",
    active: "Attivo",
    trial: "Prova",
    expired: "Scaduto",
    my_appointments: "I Miei Appuntamenti",
    client_portal: "Portale Cliente",
  },
  en: {
    home: "Home",
    dashboard: "Dashboard",
    services: "Services",
    staff: "Team",
    clients: "Clients",
    appointments: "Appointments",
    calendar: "Calendar",
    reviews: "Reviews",
    settings: "Settings",
    logout: "Logout",
    login: "Login",
    book_now: "Book Now",
    loading: "Loading...",
    error: "Error",
    success: "Success",
    cancel: "Cancel",
    save: "Save",
    delete: "Delete",
    edit: "Edit",
    add: "Add",
    search: "Search...",
    no_results: "No results",
    welcome_back: "Welcome back",
    today_appointments: "Today's Appointments",
    total_clients: "Total Clients",
    monthly_revenue: "Monthly Revenue",
    no_show_rate: "No-Show Rate",
    popular_services: "Popular Services",
    new_clients: "New Clients",
    select_service: "Select Service",
    select_date: "Select Date",
    select_time: "Select Time",
    your_info: "Your Info",
    confirm_booking: "Confirm Booking",
    booking_confirmed: "Booking Confirmed!",
    name: "Name",
    email: "Email",
    phone: "Phone",
    notes: "Notes",
    duration: "Duration",
    price: "Price",
    category: "Category",
    status: "Status",
    date: "Date",
    time: "Time",
    actions: "Actions",
    pending: "Pending",
    awaiting_confirmation: "Awaiting Confirmation",
    confirmed: "Confirmed",
    completed: "Completed",
    cancelled: "Cancelled",
    no_show: "No Show",
    unconfirmed: "Unconfirmed",
    minutes: "minutes",
    any_staff: "First Available",
    closed: "Closed",
    admin_panel: "Admin Panel",
    super_admin: "Super Admin",
    tenants: "Clients",
    subscriptions: "Subscriptions",
    create_salon: "New Business",
    manage_salons: "Manage Clients",
    active: "Active",
    trial: "Trial",
    expired: "Expired",
    my_appointments: "My Appointments",
    client_portal: "Client Portal",
  }
};

// Language Context
const LanguageContext = createContext();

export const useLanguage = () => useContext(LanguageContext);

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem("language") || "it";
  });

  const t = useCallback((key) => {
    return translations[language]?.[key] || translations.it[key] || key;
  }, [language]);

  const changeLanguage = (lang) => {
    setLanguage(lang);
    localStorage.setItem("language", lang);
  };

  return (
    <LanguageContext.Provider value={{ language, t, changeLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Auth Context
const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    // Create a timeout promise
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('Auth check timeout')), 10000); // 10 second timeout
    });

    try {
      // Race between the auth check and timeout
      const response = await Promise.race([
        axios.get(`${API}/auth/me`, {
          withCredentials: true,
          timeout: 8000 // 8 second axios timeout
        }),
        timeoutPromise
      ]);
      setUser(response.data);
    } catch (error) {
      console.error("Auth check failed:", error.message || error);
      setUser(null);
      
      // If it's a timeout or network error, and we're on a protected route,
      // the ProtectedRoute component will redirect to login
    } finally {
      setLoading(false);
    }
  }, []);

  const checkSubscription = useCallback(async () => {
    if (!user || user.role !== "salon_admin") {
      return null;
    }
    try {
      const response = await axios.get(`${API}/subscription/status`, {
        withCredentials: true
      });
      setSubscriptionStatus(response.data);
      return response.data;
    } catch (error) {
      console.error("Error checking subscription:", error);
      return null;
    }
  }, [user]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Refresh user profile (and tenant features) when the tab becomes visible
  // again — guarantees that plan/feature updates done by SuperAdmin in
  // another tab/window are reflected without forcing a full logout.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        checkAuth();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onVisibility);
    };
  }, [checkAuth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
    setSubscriptionStatus(null);
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, checkAuth, subscriptionStatus, checkSubscription }}>
      {children}
    </AuthContext.Provider>
  );
};

// Theme Context
const ThemeContext = createContext();

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem("theme") || "light";
  });

  useEffect(() => {
    document.documentElement.classList.remove("light", "dark");
    document.documentElement.classList.add(theme);
    localStorage.setItem("theme", theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === "light" ? "dark" : "light");
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// Protected Route with Subscription Check
const ProtectedRoute = ({ children, requiredRoles = [] }) => {
  const { user, loading, subscriptionStatus, checkSubscription } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [checkingSubscription, setCheckingSubscription] = useState(false);

  useEffect(() => {
    // Check subscription for salon_admin on mount
    if (user && user.role === "salon_admin" && !subscriptionStatus && !checkingSubscription) {
      setCheckingSubscription(true);
      checkSubscription().finally(() => setCheckingSubscription(false));
    }
  }, [user]);

  // Redirect to subscription-required if expired
  useEffect(() => {
    if (user && user.role === "salon_admin" && subscriptionStatus && !subscriptionStatus.can_access) {
      navigate("/subscription-required", { replace: true });
    }
  }, [subscriptionStatus, user, navigate]);

  if (loading || checkingSubscription) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse-glow">
          <div className="w-12 h-12 rounded-full bg-accent/20 flex items-center justify-center">
            <div className="w-6 h-6 rounded-full bg-accent"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  // Redirect clients to their portal from admin routes
  if (user.role === "client" && requiredRoles.length > 0 && !requiredRoles.includes("client")) {
    return <Navigate to="/my-appointments" replace />;
  }

  if (requiredRoles.length > 0 && !requiredRoles.includes(user.role)) {
    // Clients go to their portal, sales to their dashboard, others to dashboard
    if (user.role === "client") {
      return <Navigate to="/my-appointments" replace />;
    }
    if (user.role === "sales") {
      return <Navigate to="/sales-dashboard" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return <TierProvider user={user}>{children}</TierProvider>;
};

// App Router
function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/dentisti" element={<VerticalLanding sector={dentistiSector} />} />
      <Route path="/dentista" element={<DentistaLanding />} />
      <Route path="/estetica" element={<VerticalLanding sector={esteticaSector} />} />
      <Route path="/benessere-posturale" element={<BenesserePosturaleLanding />} />
      {/* Backwards-compatible redirect: /fisioterapia → /benessere-posturale */}
      <Route path="/fisioterapia" element={<Navigate to="/benessere-posturale" replace />} />
      <Route path="/hair" element={<VerticalLanding sector={hairSector} />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/demo" element={<InteractiveDemoPage />} />
      <Route path="/demo-live" element={<DemoLive />} />
      <Route path="/demo-odonto" element={<DemoOdonto />} />
      <Route path="/demo-benessere-posturale" element={<DemoBenesserePosturale />} />
      <Route path="/demo-estetica" element={<DemoEstetica />} />
      <Route path="/demo-hair" element={<DemoHair />} />
      <Route path="/demo-fisioterapia" element={<Navigate to="/demo-benessere-posturale" replace />} />
      <Route path="/demo-fisio" element={<Navigate to="/demo-benessere-posturale" replace />} />
      <Route path="/onboarding" element={<OnboardingPage />} />
      <Route path="/magic-link/:token" element={<MagicLinkVerify />} />
      <Route path="/register" element={<RegisterSalon />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/cookie-policy" element={<CookiePolicy />} />
      <Route path="/terms-conditions" element={<TermsConditions />} />
      <Route path="/dpa" element={<DataProcessingAgreement />} />
      <Route path="/trust-security" element={<TrustSecurity />} />
      <Route path="/book/:slug" element={<BookingPage />} />
      <Route path="/book/:slug/payment-success" element={<PaymentSuccessPage />} />
      <Route path="/confirm/:token" element={<ConfirmAppointment />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/subscription-required" element={<SubscriptionRequiredPage />} />
      <Route path="/sign/:token" element={<PublicSignPage />} />
      <Route path="/exercises/:token" element={<PublicExerciseRoutine />} />
      <Route path="/portal/login" element={<PatientPortalLogin />} />
      <Route path="/portal/:token" element={<PatientPortal />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <Dashboard />
        </ProtectedRoute>
      } />
      <Route path="/my-appointments" element={
        <ProtectedRoute>
          <ClientPortal />
        </ProtectedRoute>
      } />
      <Route path="/services" element={
        <ProtectedRoute requiredRoles={["salon_admin", "super_admin"]}>
          <Services />
        </ProtectedRoute>
      } />
      <Route path="/staff" element={
        <ProtectedRoute requiredRoles={["salon_admin", "super_admin"]}>
          <Staff />
        </ProtectedRoute>
      } />
      <Route path="/clients" element={
        <ProtectedRoute requiredRoles={["salon_admin", "super_admin"]}>
          <Clients />
        </ProtectedRoute>
      } />
      <Route path="/dashboard/client/:clientId" element={
        <ProtectedRoute requiredRoles={["salon_admin", "super_admin"]}>
          <ClientProfile />
        </ProtectedRoute>
      } />
      <Route path="/video-demo" element={<VideoDemoPage />} />
      <Route path="/video-demo/voce" element={<VideoDemoVoiceOverPage />} />
      <Route
        path="/dashboard/dental/migration"
        element={
          <ProtectedRoute requiredRoles={["salon_admin", "super_admin"]}>
            <DentalMigrationPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/dental/intake"
        element={
          <ProtectedRoute requiredRoles={["salon_admin", "super_admin", "staff"]}>
            <DentalIntakePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/dental/consents/templates"
        element={
          <ProtectedRoute requiredRoles={["salon_admin", "super_admin"]}>
            <ConsentTemplatesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/dental/prescriptions"
        element={
          <ProtectedRoute requiredRoles={["salon_admin", "super_admin", "staff"]}>
            <PrescriptionsHubPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/dental/trial-checklist"
        element={
          <ProtectedRoute requiredRoles={["salon_admin", "super_admin"]}>
            <DentalProTrialChecklist />
          </ProtectedRoute>
        }
      />
      <Route path="/appointments" element={
        <ProtectedRoute requiredRoles={["salon_admin", "super_admin"]}>
          <Appointments />
        </ProtectedRoute>
      } />
      <Route path="/calendar" element={
        <ProtectedRoute requiredRoles={["salon_admin", "super_admin"]}>
          <CalendarView />
        </ProtectedRoute>
      } />
      <Route path="/reviews" element={
        <ProtectedRoute requiredRoles={["salon_admin", "super_admin"]}>
          <Reviews />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute requiredRoles={["salon_admin", "super_admin"]}>
          <Settings />
        </ProtectedRoute>
      } />
      <Route path="/smartpage" element={
        <ProtectedRoute requiredRoles={["salon_admin", "super_admin"]}>
          <SmartPageEditor />
        </ProtectedRoute>
      } />
      <Route path="/analytics" element={
        <ProtectedRoute requiredRoles={["salon_admin", "super_admin"]}>
          <Analytics />
        </ProtectedRoute>
      } />
      <Route path="/whatsapp" element={
        <ProtectedRoute requiredRoles={["salon_admin", "super_admin"]}>
          <WhatsAppDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/*" element={
        <ProtectedRoute requiredRoles={["super_admin"]}>
          <SuperAdmin />
        </ProtectedRoute>
      } />
      <Route path="/sales-dashboard" element={
        <ProtectedRoute requiredRoles={["sales"]}>
          <SalesDashboard />
        </ProtectedRoute>
      } />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
            <PWAInstallBanner />
            <CookieBanner />
            <Toaster 
              position="top-right" 
              richColors 
              closeButton
              toastOptions={{
                className: "font-sans",
              }}
            />
          </BrowserRouter>
        </AuthProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
export { API, BACKEND_URL };
